import GlobalConfirmDialog from '@/core/dialogs/GlobalConfirmDialog.vue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { EIcon } from 'common/enum/misc.enum';
import { ref, type VNode } from 'vue';

export interface IConfirmDialogProps<T = unknown> {
  title: string;
  /** Strings render as text. For formatting, return Vue nodes with escaped text children, never innerHTML. */
  description: string | (() => VNode);
  confirmButtonLabel: string | null;
  declineButtonLabel: string;
  theme?: 'primary' | 'danger';
  size?: 'md' | 'lg' | 'xl';
  confirmButtonIcon?: EIcon;
  hint?: string;
  showConfirmOn?: (item: T) => boolean;
  checkInput?: IConfirmDialogPropsWithInput;
  declineOnClose?: boolean;
  hideCloseButton?: boolean;
  buttonAlignment?: 'end' | 'between';
}

interface IConfirmDialogPropsWithInput {
  inputPlaceholder: string;
  inputCheckValue: string | string[];
}

export enum EConfirmDialogResult {
  Confirm = 'confirm',
  Decline = 'decline',
  Close = 'close',
}

export type TConfirmDialogResult = `${EConfirmDialogResult}`;

export const useConfirmDialog = () => {
  const isOpen = ref(false);
  const resolvePromise = ref<((value: TConfirmDialogResult) => void) | null>(null);
  const options = ref<IConfirmDialogProps | null>(null);

  const confirmDialog = useDialog({
    component: GlobalConfirmDialog,
    props: {
      'onConfirm:dialog': () => {
        onConfirm();
      },
      'onDecline:dialog': () => {
        onDecline();
      },
      'onClose:dialog': () => {
        onClose();
      },
    },
  });

  const confirmWithResult = async (dialogOptions: IConfirmDialogProps): Promise<TConfirmDialogResult> => {
    options.value = dialogOptions;
    isOpen.value = true;

    const result = new Promise<TConfirmDialogResult>((resolve) => {
      resolvePromise.value = resolve;
    });

    confirmDialog.show(dialogOptions);
    return result;
  };

  const confirm = async (dialogOptions: IConfirmDialogProps): Promise<boolean> => {
    return (await confirmWithResult(dialogOptions)) === EConfirmDialogResult.Confirm;
  };

  const resolveDialog = (result: TConfirmDialogResult): void => {
    resolvePromise.value?.(result);
    resolvePromise.value = null;
    isOpen.value = false;
  };

  const onConfirm = () => {
    resolveDialog(EConfirmDialogResult.Confirm);
  };

  const onDecline = () => {
    resolveDialog(EConfirmDialogResult.Decline);
  };

  const onClose = () => {
    resolveDialog(EConfirmDialogResult.Close);
  };

  return {
    isOpen,
    options,
    confirm,
    confirmWithResult,
    onClose,
    onConfirm,
    onDecline,
  };
};
